import { AddRounded, DeleteRounded, InfoRounded } from '@mui/icons-material/'
import { Box, Button, ButtonGroup, Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, memo, useEffect } from 'react'
import { IFlow, INode, INodeMergeData, NodeType } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import NodeManager from '../../../../managers/NodeManager'

interface P {
    acceptedData: IAcceptedData<'xfrm_merge'>
    selectedNode: INode<'xfrm_merge'>
    flow: IFlow
    setSelected: (node: INode<'xfrm_merge'>) => void
    disable: boolean
}

const NodeMergeEditor = ({ selectedNode, setSelected, disable, flow }: P) => {
    useEffect(() => {
        if (!selectedNode.data?.mergeData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        mergeType: 'combined',
                        joinType: 'inner',
                        keys: { leftKeys: [''], rightKeys: [''] },
                        outputColumns: { leftCols: [], rightCols: [] }
                    }
                }
            })
        }
    }, [])

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index?: number) => {
        const { name, value } = event.target

        if (name === 'joinType' || name === 'mergeType') {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: { ...selectedNode.data.mergeData, [name]: value }
                }
            })
        }

        if (name === 'leftKeys' || name === 'rightKeys') {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        keys: {
                            ...selectedNode.data.mergeData.keys,
                            [name]: selectedNode.data.mergeData.keys[name].map((key, i) => i === index ? value : key)
                        }
                    }
                }
            })
        }

        if (name === 'leftCols' || name === 'rightCols') {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        outputColumns: {
                            ...selectedNode.data.mergeData.outputColumns,
                            [name]: selectedNode.data.mergeData.outputColumns[name].map((key, i) => i === index ? value : key)
                        }
                    }
                }
            })
        }
    }

    const handleAdd = (type: string) => {
        if (type === 'keys') {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData, keys: {
                            ...selectedNode.data.mergeData.keys,
                            leftKeys: [...selectedNode.data.mergeData.keys.leftKeys, ''],
                            rightKeys: [...selectedNode.data.mergeData.keys.rightKeys, '']
                        }
                    }
                }
            })
        }

        if (type === 'leftCols' || type === 'rightCols') {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        outputColumns: {
                            ...selectedNode.data.mergeData.outputColumns,
                            [type]: [...selectedNode.data.mergeData.outputColumns[type], '']
                        }
                    }
                }
            })
        }
    }

    const handleDelete = (type: 'keys' | 'leftCols' | 'rightCols', index: number) => {
        if (type === 'keys') {
            const updateLeftKey = [...selectedNode.data.mergeData.keys.leftKeys]
            const updateRightKey = [...selectedNode.data.mergeData.keys.rightKeys]
            updateLeftKey.splice(index, 1)
            updateRightKey.splice(index, 1)
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        keys: {
                            ...selectedNode.data.mergeData.keys,
                            leftKeys: updateLeftKey,
                            rightKeys: updateRightKey
                        }
                    }
                }
            })
        } else if (type === 'leftCols' || type === 'rightCols') {
            const updateCols = [...selectedNode.data.mergeData.outputColumns[type]]
            updateCols.splice(index, 1)
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    mergeData: {
                        ...selectedNode.data.mergeData,
                        outputColumns: {
                            ...selectedNode.data.mergeData.outputColumns,
                            [type]: updateCols
                        }
                    }
                }
            })
        }
    }

    const handleMode = (type: INodeMergeData['mergeType']) => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                mergeData: { ...selectedNode.data.mergeData, mergeType: type }
            }
        })
    }

    const renderButtonMode = (type: INodeMergeData['mergeType']) => (
        <Button
            variant={selectedNode.data?.mergeData?.mergeType === type ? 'contained' : undefined}
            onClick={() => handleMode(type)}
            disabled={disable}
            sx={{ flex: 1 }}>
            {type}
        </Button>
    )

    const renderInfoField = (title: string, joinType?: INodeMergeData['joinType']) => {
        if (title === null) {
            const innerInfo = 'Combines rows from both tables where there is a match in the specified columns. Rows without matching values are excluded.'
            const leftInfo = 'Includes all rows from the left table and matched rows from the right table. Unmatched rows from the right table result in NULL values.'
            const rightInfo = 'Includes all rows from the right table and matched rows from the left table. Unmatched rows from the left table result in NULL values.'
            const fullInfo = 'Combines rows from both tables, including all matched and unmatched rows. Unmatched rows from either table result in NULL values.'
            return (
                <Tooltip
                    arrow
                    title={joinType === 'inner'
                        ? innerInfo
                        : joinType === 'left'
                            ? leftInfo
                            : joinType === 'right'
                                ? rightInfo
                                : fullInfo}
                    disableHoverListener={disable}>
                    <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
                </Tooltip>
            )
        } else {
            return (
                <Tooltip arrow title={title} disableHoverListener={disable}>
                    <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
                </Tooltip>
            )
        }
    }

    const renderCombined = () => (
        <Stack spacing={3}>
            <Typography variant='h6'>Combine the two tables into a single table</Typography>
            <Typography variant='body2'>Copy all the rows from the first table and append all the rows from the second table directly below the last row of the first table.</Typography>
        </Stack>
    )

    const renderJoin = () => {
        if (!selectedNode || !selectedNode.data) return null
        const { data: { mergeData: { joinType, keys, outputColumns } } } = selectedNode
        const shapes = NodeManager.getInputShapes(selectedNode as INode<NodeType>, flow.nodes) ?? []
        const mappedShapes1 = shapes?.[0]?.shape?.items?.properties
            ? Object.keys(shapes[0].shape.items.properties)
            : shapes?.[0]?.shape?.properties
                ? Object.keys(shapes[0].shape.properties)
                : []
        const mappedShapes2 = shapes?.[1]?.shape?.items?.properties
            ? Object.keys(shapes[1].shape.items.properties)
            : shapes?.[1]?.shape?.properties
                ? Object.keys(shapes[1].shape.properties)
                : []

        return (
            <Stack spacing={3}>
                <Typography variant='h6'>Build the join operation:</Typography>
                <Stack flexDirection='row' gap={2}>
                    <Stack spacing={3} flex={1}>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            {renderInfoField(null, joinType)}
                            <Typography variant='subtitle2'>Choose the join type:</Typography>
                        </Stack>
                        <TextField
                            sx={{ width: '90%' }}
                            size='small'
                            name='joinType'
                            value={joinType}
                            disabled={disable}
                            select
                            onChange={event => handleInputChange(event)}>
                            {['inner', 'left', 'right', 'full', 'cross'].map(option => (
                                <MenuItem key={option} value={option}>
                                    {option.toUpperCase()}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <Box sx={{ flex: 1 }} />
                </Stack>
                {selectedNode.data?.mergeData?.joinType !== 'cross' && (
                    <Stack spacing={3}>
                        <Stack flexDirection='row' justifyContent='space-between'>
                            <Stack flexDirection='row' gap={1} alignSelf='flex-start' alignItems='center'>
                                {renderInfoField('The column(s) from the left table used to match rows with the right table during a join operation.')}
                                <Typography variant='subtitle2'>Left Keys:</Typography>
                            </Stack>
                            <Stack flexDirection='row' gap={1} alignSelf='flex-end' alignItems='center'>
                                {renderInfoField('The column(s) from the right table used to match rows with the left table during a join operation.')}
                                <Typography variant='subtitle2'>Right Keys:</Typography>
                            </Stack>
                        </Stack>
                        <Stack spacing={3}>
                            {keys.leftKeys.map((leftKey, index) => (
                                <Stack flexDirection='row' alignItems='center' key={`keys-${index}`}>
                                    <TextField
                                        size='small'
                                        sx={{ flex: 1 }}
                                        name='leftKeys'
                                        select={mappedShapes1.length > 0}
                                        value={leftKey ?? ''}
                                        disabled={disable}
                                        error={leftKey === ''}
                                        onChange={event => handleInputChange(event, index)}>
                                        {mappedShapes1.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Divider orientation='horizontal' sx={{ flex: 0.1 }} />
                                    <IconButton
                                        size='small'
                                        onClick={() => handleDelete('keys', index)}
                                        disabled={disable || keys.leftKeys.length === 1}>
                                        <DeleteRounded color={(disable || keys.leftKeys.length === 1) ? undefined : 'error'} />
                                    </IconButton>
                                    <Divider orientation='horizontal' sx={{ flex: 0.1 }} />
                                    <TextField
                                        size='small'
                                        sx={{ flex: 1 }}
                                        name='rightKeys'
                                        select={mappedShapes2.length > 0}
                                        value={keys.rightKeys[index] ?? ''}
                                        disabled={disable}
                                        error={keys.rightKeys[index] === ''}
                                        onChange={event => handleInputChange(event, index)}>
                                        {mappedShapes2.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            ))}
                            <Button
                                startIcon={<AddRounded />}
                                variant='text'
                                disabled={disable || (keys.leftKeys.some(x => x === '') || keys.rightKeys.some(x => x === ''))}
                                onClick={() => handleAdd('keys')}>Add Keys
                            </Button>
                        </Stack>
                    </Stack>
                )}
                <Stack spacing={3}>
                    <Stack flexDirection='row' justifyContent='space-between'>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            {renderInfoField('Choose columns from the left table that you want to retain in the final output. If no specific columns are chosen, all columns from left table will be included in the final output by default.')}
                            <Typography variant='subtitle2'>Left output columns:</Typography>
                        </Stack>
                        <Stack flexDirection='row' gap={1} justifyContent='flex-end' alignItems='center'>
                            {renderInfoField('Choose columns from the right table that you want to include in the final output. If no specific columns are chosen, all columns from right table will be included in the final output by default.')}
                            <Typography variant='subtitle2'>Right output columns:</Typography>
                        </Stack>
                    </Stack>
                    <Stack flexDirection='row' gap={3} justifyContent='space-between' alignItems='flex-start'>
                        <Stack spacing={2} flex={1}>
                            {outputColumns.leftCols.length !== 0 && (
                                <>
                                    {outputColumns.leftCols.map((leftCol, index) => (
                                        <Stack key={`leftCol-${index}`} flexDirection='row' gap={1} justifyContent='flex-start'>
                                            <IconButton
                                                size='small'
                                                onClick={() => handleDelete('leftCols', index)}
                                                disabled={disable}>
                                                <DeleteRounded color={disable ? undefined : 'error'} />
                                            </IconButton>
                                            <TextField
                                                sx={{ width: '75%' }}
                                                size='small'
                                                name='leftCols'
                                                select={mappedShapes1.length > 0}
                                                value={leftCol ?? ''}
                                                disabled={disable}
                                                error={leftCol === ''}
                                                onChange={event => handleInputChange(event, index)}>
                                                {mappedShapes1.map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Stack>
                                    ))}
                                </>
                            )}
                            <Button
                                sx={{ alignSelf: 'flex-start' }}
                                startIcon={<AddRounded />}
                                variant='text'
                                disabled={disable || outputColumns.leftCols.some(x => x === '')}
                                onClick={() => handleAdd('leftCols')}>
                                add left column
                            </Button>
                        </Stack>
                        <Stack spacing={2} flex={1}>
                            {outputColumns.rightCols.length !== 0 && (
                                <>
                                    {outputColumns.rightCols.map((rightCol, index) => (
                                        <Stack key={`rightCol-${index}`} flexDirection='row' gap={1} justifyContent='flex-end'>
                                            <TextField
                                                sx={{ width: '75%' }}
                                                size='small'
                                                name='rightCols'
                                                select={mappedShapes2.length > 0}
                                                value={rightCol ?? ''}
                                                disabled={disable}
                                                error={rightCol === ''}
                                                onChange={event => handleInputChange(event, index)}>
                                                {mappedShapes2.map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <IconButton
                                                size='small'
                                                onClick={() => handleDelete('rightCols', index)}
                                                disabled={disable}>
                                                <DeleteRounded color={disable ? undefined : 'error'} />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </>
                            )}
                            <Button
                                sx={{ alignSelf: 'flex-end' }}
                                endIcon={<AddRounded />}
                                variant='text'
                                disabled={disable || outputColumns.rightCols.some(x => x === '')}
                                onClick={() => handleAdd('rightCols')}>
                                add right column
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack flex={1} spacing={3} justifyContent='center'>
            <Stack alignItems='center'>
                <ButtonGroup sx={{ width: 200 }} size='small'>
                    {renderButtonMode('combined')}
                    {renderButtonMode('join')}
                </ButtonGroup>
            </Stack>
            {(selectedNode.data?.mergeData?.mergeType === 'combined')
                ? renderCombined()
                : selectedNode.data?.mergeData?.mergeType === 'join'
                    ? renderJoin()
                    : null}
        </Stack>
    )
}

export default memo(NodeMergeEditor)
